import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ThemeToggle } from '../components/ThemeToggle';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { 
  X, 
  Clock, 
  Save, 
  History, 
  AlertTriangle, 
  RefreshCw,
  Send,
  ChevronLeft,
  Mail,
  CheckCircle,
  Loader2,
  ArrowRight,
  Sparkles
} from 'lucide-react';

// Session timeout in minutes
const SESSION_TIMEOUT_MINUTES = 25;
const WARNING_BEFORE_TIMEOUT_MINUTES = 2;

// Helper function to check if a message contains final JSON results
const containsJsonResults = (content) => {
  if (!content || typeof content !== 'string') return false;
  // v9: Check for completion/profile markers
  if (content.includes('[EDGE_DIAGNOSTIC_COMPLETE]') || content.includes('[EDGE_PROFILE_END]')) return true;
  if (content.includes('[CORE_DIAGNOSTIC_COMPLETE]') || content.includes('[CORE_PROFILE_END]')) return true;
  if (content.includes('[EDGE_PROFILE_START]') || content.includes('[CORE_PROFILE_START]')) return true;
  // v8/legacy: JSON with known keys
  const hasJsonBlock = content.includes('```json') && (
    content.includes('primary_fear_cluster') || 
    content.includes('edge_profile') ||
    content.includes('primary_archetype') ||
    content.includes('bayesian_results')
  );
  const hasRawJson = (
    content.includes('"primary_fear_cluster"') || 
    content.includes('"primary_archetype"') ||
    content.includes('"edge_profile"') ||
    content.includes('"bayesian_results"')
  );
  const jsonMatch = content.match(/```json[\s\S]{500,}```/);
  return hasJsonBlock || hasRawJson || !!jsonMatch;
};

// Helper to extract the non-JSON part of the final message
const extractCompletionMessage = (content) => {
  if (!content) return '';
  // Get everything before the JSON block
  const beforeJson = content.split('```json')[0].trim();
  return beforeJson || "Thank you for completing the assessment.";
};

function AssessmentFlow() {
  const { type, assessmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [stage, setStage] = useState(assessmentId && assessmentId !== 'start' ? 'loading' : 'instruction');
  const [assessment, setAssessment] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [estimatedQuestions, setEstimatedQuestions] = useState(type === 'fear' ? 25 : 27);
  
  // New state for enhanced features
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingPhase, setThinkingPhase] = useState('');
  const [showHistorySheet, setShowHistorySheet] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [sendReminderEmail, setSendReminderEmail] = useState(true);
  const [exitLoading, setExitLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(SESSION_TIMEOUT_MINUTES * 60);
  const [questionTransition, setQuestionTransition] = useState(false);
  const [isAssessmentComplete, setIsAssessmentComplete] = useState(false);
  
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Load in-progress assessment if assessmentId is provided
  useEffect(() => {
    const loadExistingAssessment = async () => {
      if (assessmentId && assessmentId !== 'start') {
        try {
          setLoading(true);
          const response = await axios.get(`/api/assessments/${assessmentId}`);
          // The API returns { assessment: { ... } }
          if (response.data && response.data.assessment) {
            const existingAssessment = response.data.assessment;
            
            // Check if assessment is already marked as completed in the database
            if (existingAssessment.status === 'completed') {
              setAssessment(existingAssessment);
              setProgress(100);
              setStage('complete');
              // Redirect to results after showing completion screen
              setTimeout(() => {
                navigate(`/results/${existingAssessment.id}`);
              }, 2500);
              return;
            }
            
            // Validate the assessment has valid conversation data
            const hasSystemMessage = existingAssessment.conversationLog?.some(msg => msg.role === 'system');
            const assistantMessages = existingAssessment.conversationLog?.filter(msg => msg.role === 'assistant') || [];
            const lastAiMessage = assistantMessages[assistantMessages.length - 1];
            
            // If assessment is corrupted (no system message or no AI responses), start fresh
            if (!hasSystemMessage || assistantMessages.length === 0) {
              console.warn('Assessment appears corrupted, redirecting to start fresh');
              toast.info('Starting a fresh assessment');
              navigate(`/assessment/${type}/start`, { replace: true });
              return;
            }
            
            setAssessment(existingAssessment);
            
            // Calculate progress based on question count
            // The first 5 questions are staging, progress starts after that
            const questionCount = existingAssessment.questionCount || 0;
            const totalQuestions = type === 'fear' ? 25 : 27;
            const stagingQuestions = 6;
            const effectiveQuestionCount = Math.max(0, questionCount - stagingQuestions);
            const diagnosticQuestions = totalQuestions - stagingQuestions;
            const calculatedProgress = effectiveQuestionCount > 0 
              ? Math.min(Math.round((effectiveQuestionCount / diagnosticQuestions) * 100), 95)
              : 0;
            setProgress(calculatedProgress);
            
            // Check if the last AI message contains JSON results (assessment complete but status not updated)
            if (lastAiMessage && containsJsonResults(lastAiMessage.content)) {
              // Assessment is complete - show completion screen
              setProgress(100);
              setStage('complete');
              // Redirect to results after showing completion screen
              setTimeout(() => {
                navigate(`/results/${existingAssessment.id}`);
              }, 2500);
              return;
            }
            
            // Set the current question to the last AI message
            if (lastAiMessage) {
              setCurrentQuestion(lastAiMessage.content);
            }
            
            setStage('assessment');
          }
        } catch (error) {
          console.error('Error loading assessment:', error);
          // If assessment not found or error, start fresh
          setStage('instruction');
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadExistingAssessment();
  }, [assessmentId, type, navigate]);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [assessment?.conversationLog, isThinking]);
  
  // Focus input after question transition
  useEffect(() => {
    if (!loading && !questionTransition && inputRef.current && stage === 'assessment') {
      inputRef.current.focus();
    }
  }, [loading, questionTransition, stage]);
  
  // Session timeout management
  const resetSessionTimer = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setTimeRemaining(SESSION_TIMEOUT_MINUTES * 60);
    setShowTimeoutWarning(false);
    
    // Set warning timer (show warning 2 minutes before timeout)
    const warningTime = (SESSION_TIMEOUT_MINUTES - WARNING_BEFORE_TIMEOUT_MINUTES) * 60 * 1000;
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            handleSessionTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningTime);
    
    // Set actual timeout
    timeoutRef.current = setTimeout(() => {
      handleSessionTimeout();
    }, SESSION_TIMEOUT_MINUTES * 60 * 1000);
  }, []);
  
  const handleSessionTimeout = async () => {
    // Auto-save and redirect
    if (assessment?.id) {
      try {
        await axios.post(`/api/assessments/${assessment.id}/save-exit`, { sendReminder: true });
        toast.info('Session timed out. Your progress has been saved.');
      } catch (error) {
        console.error('Error saving on timeout:', error);
      }
    }
    navigate('/dashboard');
  };
  
  const extendSession = async () => {
    try {
      await axios.post(`/api/assessments/${assessment.id}/extend-session`);
      resetSessionTimer();
      setShowTimeoutWarning(false);
      toast.success('Session extended by 25 minutes');
    } catch (error) {
      toast.error('Failed to extend session');
    }
  };
  
  // Start timer when assessment begins
  useEffect(() => {
    if (stage === 'assessment' && assessment?.id) {
      resetSessionTimer();
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [stage, assessment?.id, resetSessionTimer]);
  
  const startAssessment = async () => {
    setLoading(true);
    setIsThinking(true);
    setThinkingPhase('Preparing your assessment...');
    
    try {
      const response = await axios.post('/api/assessments', { type });
      
      setThinkingPhase('Generating your first question...');
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for UX
      
      setAssessment(response.data.assessment);
      setCurrentQuestion(response.data.firstQuestion);
      setStage('assessment');
      calculateProgress(response.data.assessment);
      setLastError(null);
      setRetryCount(0);
    } catch (error) {
      setLastError(error.response?.data?.detail || 'Failed to start assessment');
      toast.error(error.response?.data?.detail || 'Failed to start assessment');
    } finally {
      setLoading(false);
      setIsThinking(false);
      setThinkingPhase('');
    }
  };
  
  const calculateProgress = (assessmentData) => {
    const questionCount = assessmentData.questionCount || 0;
    // The first 5 questions are information gathering (name, role, industry, etc.)
    // Progress should only start after these staging questions
    const stagingQuestions = 6;
    const effectiveQuestionCount = Math.max(0, questionCount - stagingQuestions);
    const diagnosticQuestions = estimatedQuestions - stagingQuestions;
    const progress = effectiveQuestionCount > 0 
      ? Math.min((effectiveQuestionCount / diagnosticQuestions) * 100, 95) 
      : 0;
    setProgress(progress);
  };
  
  const submitResponse = async (retrying = false) => {
    if (!userInput.trim()) {
      toast.error('Please provide a response');
      return;
    }
    
    // Reset session timer on activity
    resetSessionTimer();
    
    setLoading(true);
    setIsThinking(true);
    setThinkingPhase('Processing your response...');
    
    const responseText = userInput;
    if (!retrying) {
      setUserInput('');
    }
    
    // Calculate if we're near the end of the diagnostic
    const currentQuestionCount = assessment?.questionCount || 0;
    const totalExpected = type === 'fear' ? 25 : 27;
    const isNearEnd = currentQuestionCount >= totalExpected - 3;
    
    try {
      // Simulate thinking phases for better UX - adjust messages based on progress
      setTimeout(() => setThinkingPhase('Analyzing your answer...'), 800);
      
      if (isNearEnd) {
        // Near the end - show closing/analysis messages
        setTimeout(() => setThinkingPhase('Synthesizing your responses...'), 2000);
        setTimeout(() => setThinkingPhase('Generating your personalized insights...'), 4000);
        setTimeout(() => setThinkingPhase('Preparing your comprehensive results...'), 7000);
      } else {
        setTimeout(() => setThinkingPhase('Formulating next question...'), 2000);
      }
      
      const response = await axios.post(
        `/api/assessments/${assessment.id}/respond`,
        { response: responseText }
      );
      
      const updatedAssessment = await axios.get(`/api/assessments/${assessment.id}`);
      
      // Smooth transition between questions
      setQuestionTransition(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setAssessment(updatedAssessment.data.assessment);
      calculateProgress(updatedAssessment.data.assessment);
      
      if (response.data.isComplete) {
        setProgress(100);
        setStage('complete');
        setTimeout(() => {
          navigate(`/results/${type}/${assessment.id}`);
        }, 2500);
      } else {
        setCurrentQuestion(response.data.nextQuestion);
      }
      
      setLastError(null);
      setRetryCount(0);
      
      setTimeout(() => setQuestionTransition(false), 300);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to submit response';
      const statusCode = error.response?.status;
      setLastError({ message: errorMessage, response: responseText });
      
      // Only auto-retry on network errors or 5xx — never retry 4xx (client errors)
      if (!statusCode || statusCode >= 500) {
        if (retryCount < 2) {
          setRetryCount(prev => prev + 1);
          toast.error(`Request failed. Retrying... (${retryCount + 1}/3)`);
          setTimeout(() => submitResponse(true), 1500);
        } else {
          toast.error('Connection issue. Your progress is saved. Please try again.');
          setUserInput(responseText);
        }
      } else {
        // 4xx error — don't retry, show error and restore input
        toast.error(errorMessage);
        setUserInput(responseText);
      }
    } finally {
      setLoading(false);
      setIsThinking(false);
      setThinkingPhase('');
    }
  };
  
  const handleRetry = () => {
    if (lastError?.response) {
      setUserInput(lastError.response);
      setRetryCount(0);
      submitResponse(true);
    }
  };
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitResponse();
    }
  };
  
  const handleExitAndSave = async () => {
    setExitLoading(true);
    try {
      const response = await axios.post(`/api/assessments/${assessment.id}/save-exit`, {
        sendReminder: sendReminderEmail
      });
      
      setShowExitDialog(false);
      
      if (response.data.emailSent) {
        toast.success('Progress saved! Check your email for a reminder to continue.');
      } else {
        toast.success('Progress saved! You can continue from your dashboard anytime.');
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save progress. Please try again.');
    } finally {
      setExitLoading(false);
    }
  };
  
  const formatTimeRemaining = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const getInstructions = () => {
    if (type === 'fear') {
      return {
        title: 'EDGE Diagnostic',
        description: 'Understand your unique pressures and how they shape your leadership responses',
        duration: '10-15 minutes',
        questions: '~20 questions',
        intro: [
          "Recent studies show that professionals increasingly view AI as their biggest career uncertainty—whether as threat, opportunity, or both. This diagnostic uses an adaptive AI model to explore the unique pressures you're experiencing in this evolving landscape.",
          "The AI is deliberately direct in its questioning and may challenge your thinking. This isn't intended to offend, but designed to provoke genuine reflection. These pressures may feel positive or uncomfortable; understanding them helps define your leadership edge."
        ],
        points: [
          'Answer honestly - this is for your benefit',
          'There are no right or wrong answers',
          "This isn't a test of your AI knowledge or resilience",
          'Choose the option that feels most true, even if none are perfect',
          'We will identify your pressure patterns and triggers',
          'You can pause and resume at any time'
        ]
      };
    } else {
      return {
        title: 'CORE Diagnostic',
        description: 'Discover your leadership archetype - both your Stated self and Core patterns',
        duration: '15-30 minutes',
        questions: '20-25 questions',
        intro: [],
        points: [
          'Think about your actual behavior, not aspirations',
          'Choose based on what you do, not what you think you should do',
          'We will identify your Stated and Core archetypes',
          'This builds on your EDGE diagnostic results',
          'You can pause and resume at any time'
        ]
      };
    }
  };
  
  const instructions = getInstructions();
  
  // Typing indicator component
  const TypingIndicator = () => (
    <div className="flex justify-start">
      <div className="max-w-[80%] rounded-[var(--radius)] p-4 bg-card border border-border">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <motion.div 
              className="w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0 }}
            />
            <motion.div 
              className="w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
            />
            <motion.div 
              className="w-2 h-2 bg-primary rounded-full"
              animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
            />
          </div>
          <AnimatePresence mode="wait">
            <motion.span 
              key={thinkingPhase}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="text-sm text-muted-foreground"
            >
              {thinkingPhase}
            </motion.span>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
  
  // Thinking animation component (brain visualization)
  const ThinkingAnimation = () => (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex items-center justify-center py-4"
    >
      <div className="relative">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 rounded-full border-2 border-primary/20 border-t-primary"
        />
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="absolute inset-0 flex items-center justify-center"
        >
          <span className="text-lg">🧠</span>
        </motion.div>
      </div>
    </motion.div>
  );
  
  // History panel content
  const renderHistoryContent = () => {
    const messages = assessment?.conversationLog?.filter(msg => msg.role !== 'system') || [];
    const pairs = [];
    
    for (let i = 0; i < messages.length; i += 2) {
      if (messages[i] && messages[i + 1]) {
        pairs.push({
          question: messages[i].role === 'assistant' ? messages[i].content : messages[i + 1]?.content,
          answer: messages[i].role === 'user' ? messages[i].content : messages[i + 1]?.content,
          index: Math.floor(i / 2) + 1
        });
      }
    }
    
    return (
      <div className="space-y-6 py-4">
        {pairs.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No previous answers yet</p>
        ) : (
          pairs.map((pair, idx) => (
            <div key={idx} className="border-b border-border pb-4 last:border-0">
              <div className="flex items-start gap-2 mb-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium">
                  {pair.index}
                </span>
                <p className="text-sm font-medium">{pair.question}</p>
              </div>
              <div className="ml-8 p-3 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">{pair.answer}</p>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };
  
  if (stage === 'instruction') {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="container-custom py-4">
            <div className="flex items-center justify-between">
              <a href="/">
                <img src="/brand/logo-gradient-dark.svg" alt="Secret Sauce" className="h-8 w-auto dark:hidden" />
                <img src="/brand/logo-gradient-white.svg" alt="Secret Sauce" className="h-8 w-auto hidden dark:block" />
              </a>
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-muted-foreground hover:text-foreground transition-smooth flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Dashboard
              </button>
            </div>
          </div>
        </header>
        
        <main className="container-custom py-12 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-[var(--radius)] p-8 elev-1"
          >
            <div className="mb-6">
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                {instructions.title}
              </span>
              <h2 className="h2 mb-4">{instructions.description}</h2>
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {instructions.duration}</span>
                <span>• {instructions.questions}</span>
              </div>
            </div>
            
            <div className="space-y-4 mb-8">
              <h3 className="font-semibold">Before you begin:</h3>
              
              {/* Intro paragraphs */}
              {instructions.intro && instructions.intro.length > 0 && (
                <div className="space-y-4 text-muted-foreground">
                  {instructions.intro.map((paragraph, index) => (
                    <p key={index} className="body leading-relaxed">{paragraph}</p>
                  ))}
                </div>
              )}
              
              {/* Checklist points */}
              <ul className="space-y-3 mt-6">
                {instructions.points.map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="body">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {lastError && (
              <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 text-destructive mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Error</span>
                </div>
                <p className="text-sm text-destructive/80">{lastError}</p>
                <Button variant="outline" size="sm" onClick={startAssessment} className="mt-2">
                  <RefreshCw className="w-4 h-4 mr-1" /> Try Again
                </Button>
              </div>
            )}
            
            <Button
              onClick={startAssessment}
              disabled={loading}
              className="w-full h-12"
              data-testid="begin-assessment"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {thinkingPhase || 'Starting...'}
                </>
              ) : (
                'Begin Assessment'
              )}
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }
  
  if (stage === 'assessment') {
    const assistantMessages = assessment?.conversationLog?.filter(msg => msg.role === 'assistant') || [];
    const actualQuestionCount = assistantMessages.length;
    
    // The first 5 questions are information gathering (name, role, industry, etc.)
    // Progress and display should account for this
    const informationGatheringQuestions = 6;
    const effectiveQuestionCount = Math.max(0, actualQuestionCount - informationGatheringQuestions);
    const diagnosticQuestions = estimatedQuestions - informationGatheringQuestions;
    const displayQuestionNumber = effectiveQuestionCount > 0 ? effectiveQuestionCount : null;
    
    // Check if the last assistant message contains JSON results (assessment complete)
    const lastAssistantMessage = assistantMessages[assistantMessages.length - 1];
    const assessmentComplete = lastAssistantMessage && containsJsonResults(lastAssistantMessage.content);
    
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="container-custom py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-4">
                <h1 className="font-serif text-xl font-semibold">{instructions.title}</h1>
                
                {/* History button */}
                <Sheet open={showHistorySheet} onOpenChange={setShowHistorySheet}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-muted-foreground" data-testid="view-history-btn">
                      <History className="w-4 h-4 mr-1" /> History
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[400px] sm:w-[500px]">
                    <SheetHeader>
                      <SheetTitle>Previous Answers</SheetTitle>
                      <SheetDescription>
                        Review your responses (read-only)
                      </SheetDescription>
                    </SheetHeader>
                    {renderHistoryContent()}
                  </SheetContent>
                </Sheet>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">
                  {assessmentComplete 
                    ? <span className="text-success flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Complete</span>
                    : displayQuestionNumber 
                      ? displayQuestionNumber < diagnosticQuestions 
                        ? `Question ${displayQuestionNumber} of ~${diagnosticQuestions}`
                        : `Wrapping up...`
                      : `Getting to know you...`
                  }
                </span>
                
                {/* Theme Toggle */}
                <ThemeToggle />
                
                {/* Exit and Save button - only show if not complete */}
                {!assessmentComplete && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowExitDialog(true)}
                    className="text-muted-foreground"
                    data-testid="exit-save-btn"
                  >
                    <Save className="w-4 h-4 mr-1" /> Save & Exit
                  </Button>
                )}
                
                {/* Back to Dashboard - show when complete */}
                {assessmentComplete && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => navigate('/dashboard')}
                    className="text-muted-foreground"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Dashboard
                  </Button>
                )}
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                className={`h-full ${assessmentComplete ? 'bg-success' : 'bg-primary'}`}
                initial={{ width: 0 }}
                animate={{ width: assessmentComplete ? '100%' : `${effectiveQuestionCount > 0 ? Math.min((effectiveQuestionCount / diagnosticQuestions) * 100, 95) : 0}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-y-auto">
          <div className="container-custom py-8 max-w-3xl">
            <div className="space-y-6">
              <AnimatePresence mode="popLayout">
                {assessment?.conversationLog
                  ?.filter(msg => msg.role !== 'system')
                  .map((msg, index, arr) => {
                    // Check if this is the last assistant message with JSON results
                    const isLastMessage = index === arr.length - 1;
                    const hasJsonResults = msg.role === 'assistant' && containsJsonResults(msg.content);
                    
                    // If this is the final message with JSON, show completion UI instead
                    if (hasJsonResults && isLastMessage) {
                      const completionMessage = extractCompletionMessage(msg.content);
                      return (
                        <motion.div
                          key={`msg-${index}`}
                          initial={{ opacity: 0, y: 20, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          className="flex justify-start"
                        >
                          <div className="max-w-[80%] rounded-[var(--radius)] p-4 bg-card border border-border">
                            <p className="body whitespace-pre-wrap mb-4">{completionMessage}</p>
                            <div className="p-4 bg-success/10 rounded-lg border border-success/20">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-success" />
                                </div>
                                <div>
                                  <p className="font-semibold text-success">Diagnostic Complete!</p>
                                  <p className="text-sm text-muted-foreground">Your results are ready</p>
                                </div>
                              </div>
                              <Button 
                                onClick={() => navigate(`/results/${assessment.id}`)}
                                className="w-full"
                                data-testid="view-results-btn"
                              >
                                <Sparkles className="w-4 h-4 mr-2" />
                                View Your Results
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      );
                    }
                    
                    // Regular message display
                    return (
                      <motion.div
                        key={`msg-${index}`}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <motion.div 
                          className={`max-w-[80%] rounded-[var(--radius)] p-4 ${
                            msg.role === 'user'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-card border border-border'
                          }`}
                          layout
                        >
                          <p className="body whitespace-pre-wrap">{msg.content}</p>
                        </motion.div>
                      </motion.div>
                    );
                  })}
              </AnimatePresence>
              
              {/* Thinking/Typing indicator */}
              <AnimatePresence>
                {isThinking && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <TypingIndicator />
                  </motion.div>
                )}
              </AnimatePresence>
              
              <div ref={chatEndRef} />
            </div>
          </div>
        </main>
        
        {/* Error retry banner */}
        <AnimatePresence>
          {lastError && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="border-t border-destructive/20 bg-destructive/5 py-3"
            >
              <div className="container-custom max-w-3xl flex items-center justify-between">
                <div className="flex items-center gap-2 text-destructive">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm">{lastError.message}</span>
                </div>
                <Button variant="outline" size="sm" onClick={handleRetry}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Retry
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Input area - only show if assessment is not complete */}
        {!assessmentComplete && (
          <div className="border-t border-border bg-card sticky bottom-0">
            <div className="container-custom py-4 max-w-3xl">
              <div className="flex gap-3">
                <textarea
                  ref={inputRef}
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type your response... (Press Enter to send)"
                  disabled={loading}
                  className="flex-1 min-h-[60px] max-h-[200px] px-4 py-3 rounded-[var(--radius)] border border-input bg-background focus-ring resize-none disabled:opacity-50"
                  data-testid="response-input"
                />
                <Button
                  onClick={() => submitResponse(false)}
                  disabled={loading || !userInput.trim()}
                  className="px-6 h-[60px]"
                  data-testid="submit-response"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
                <CheckCircle className="w-3 h-3 text-success" />
                Your progress is automatically saved
              </p>
            </div>
          </div>
        )}
        
        {/* Completion footer - show when assessment is complete */}
        {assessmentComplete && (
          <div className="border-t border-success/20 bg-success/5 sticky bottom-0">
            <div className="container-custom py-4 max-w-3xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <span className="text-sm font-medium">Your diagnostic is complete!</span>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/dashboard')}
                  >
                    Back to Dashboard
                  </Button>
                  <Button 
                    onClick={() => navigate(`/results/${assessment.id}`)}
                    data-testid="view-results-footer-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    View Results
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Exit and Save Dialog */}
        <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save and Exit?</DialogTitle>
              <DialogDescription>
                Your progress will be saved. You can continue from where you left off at any time.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Save className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">Progress: {Math.max(0, (assessment?.questionCount || 0) - 6)} diagnostic questions answered</p>
                  <p className="text-sm text-muted-foreground">
                    ~{Math.round(progress)}% complete
                  </p>
                </div>
              </div>
              
              <div className="mt-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendReminderEmail}
                    onChange={(e) => setSendReminderEmail(e.target.checked)}
                    className="w-4 h-4 rounded border-input"
                  />
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Send me a reminder email to continue</span>
                  </div>
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExitDialog(false)}>
                Continue Assessment
              </Button>
              <Button onClick={handleExitAndSave} disabled={exitLoading}>
                {exitLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save & Exit
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Session Timeout Warning Dialog */}
        <Dialog open={showTimeoutWarning} onOpenChange={setShowTimeoutWarning}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-warning">
                <Clock className="w-5 h-5" />
                Session Expiring Soon
              </DialogTitle>
              <DialogDescription>
                Your session will timeout due to inactivity. Would you like to continue?
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-center">
              <div className="text-4xl font-bold text-warning mb-2">
                {formatTimeRemaining(timeRemaining)}
              </div>
              <p className="text-sm text-muted-foreground">
                Your progress is automatically saved
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowTimeoutWarning(false);
                setShowExitDialog(true);
              }}>
                Save & Exit
              </Button>
              <Button onClick={extendSession}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Continue (+25 min)
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  if (stage === 'complete') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card border border-border rounded-[var(--radius)] p-8 max-w-md text-center elev-2"
        >
          <motion.div 
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6"
          >
            <CheckCircle className="w-8 h-8 text-success" />
          </motion.div>
          <h2 className="h2 text-2xl mb-4">Assessment Complete!</h2>
          <p className="body mb-6">
            Analyzing your responses and preparing your personalized results...
          </p>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary"
              animate={{ width: ['0%', '100%'] }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Redirecting to your results...
          </p>
        </motion.div>
      </div>
    );
  }
  
  return null;
}

export default AssessmentFlow;
